import {
  collection,
  deleteDoc,
  doc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

// ---------- Refs / queries ----------

export const linkedCalendarsRef = (uid) =>
  collection(db, "usersAvailability", uid, "linkedCalendars");

export const linkedCalendarRef = (uid, calendarId) =>
  doc(db, "usersAvailability", uid, "linkedCalendars", calendarId);

/**
 * Provider abstraction (calendar-cache-privacy P4).
 *
 * Read the provider id from a `linkedCalendars/{id}` doc, defaulting to
 * `"google"` when the field is absent. Existing alpha-cohort docs were
 * written before P4 and lack the field; this shim avoids backfill for the
 * single-provider release. Drop once we're confident no pre-P4 docs remain
 * in production, OR once a non-Google provider is added (whichever forces
 * the issue).
 *
 * @param {{ provider?: string } | null | undefined} linkedCalendar
 * @returns {string}
 */
export function getLinkedCalendarProvider(linkedCalendar) {
  return linkedCalendar?.provider || "google";
}

export const availabilityEventsRef = (uid) =>
  collection(db, "usersAvailability", uid, "events");

export const availabilityEventsByCalendarQuery = (uid, calendarId) =>
  uid && calendarId
    ? query(availabilityEventsRef(uid), where("calendarId", "==", calendarId))
    : null;

export const eventHideOverridesRef = (uid) =>
  collection(db, "usersAvailability", uid, "eventHideOverrides");

export const eventHideOverrideRef = (uid, overrideId) =>
  doc(db, "usersAvailability", uid, "eventHideOverrides", overrideId);

// ---------- Hide-override mutations (direct Firestore) ----------

/**
 * Creates a hide override doc. Caller is responsible for generating overrideId
 * (e.g. crypto.randomUUID()).
 */
export async function createHideOverride(uid, overrideId, payload) {
  await setDoc(eventHideOverrideRef(uid, overrideId), {
    ...payload,
    createdAt: serverTimestamp(),
  });
}

export async function deleteHideOverride(uid, overrideId) {
  await deleteDoc(eventHideOverrideRef(uid, overrideId));
}

// ---------- Callable wrappers ----------

export async function listUserAvailabilityCalendars() {
  const fn = httpsCallable(functions, "listUserAvailabilityCalendars");
  const res = await fn();
  return res.data?.items || [];
}

export async function linkAvailabilityCalendar({
  calendarId,
  calendarType,
  color,
  defaultVoteLevel,
}) {
  const fn = httpsCallable(functions, "linkAvailabilityCalendar");
  const payload = { calendarId, calendarType, color };
  if (defaultVoteLevel) payload.defaultVoteLevel = defaultVoteLevel;
  const res = await fn(payload);
  return res.data;
}

export async function previewAvailabilityCalendarEvents({
  calendarId,
  calendarType,
  defaultVoteLevel,
}) {
  const fn = httpsCallable(functions, "previewAvailabilityCalendarEvents");
  const result = await fn({ calendarId, calendarType, defaultVoteLevel });
  return result.data;
}

export async function unlinkAvailabilityCalendar({ calendarId }) {
  const fn = httpsCallable(functions, "unlinkAvailabilityCalendar");
  const res = await fn({ calendarId });
  return res.data;
}

/**
 * Phase 4c P4 — wipe the user's entire cached-event subtree without
 * unlinking any calendars. Returns counts of what was deleted. The next
 * watch tick will repopulate the cache from Google.
 */
export async function deleteCachedCalendarEvents() {
  const fn = httpsCallable(functions, "deleteCachedCalendarEvents");
  const res = await fn({});
  return res.data;
}

/**
 * Cache-controls follow-up — re-fetch and re-cache events for every linked
 * calendar. Pairs with deleteCachedCalendarEvents so users can restore the
 * cache immediately after wiping it (rather than waiting for the next watch
 * tick or polling fallback).
 *
 * Returns: { calendarsRefreshed, calendarsFailed, eventsProcessed, failures }
 */
export async function refreshCachedCalendarEvents() {
  const fn = httpsCallable(functions, "refreshCachedCalendarEvents");
  const res = await fn({});
  return res.data;
}

export async function updateLinkedAvailabilityCalendar({
  calendarId,
  color,
  defaultVoteLevel,
  trustEnabled,
}) {
  const fn = httpsCallable(functions, "updateLinkedAvailabilityCalendar");
  const payload = { calendarId };
  if (color !== undefined) payload.color = color;
  if (defaultVoteLevel !== undefined) payload.defaultVoteLevel = defaultVoteLevel;
  if (trustEnabled !== undefined) payload.trustEnabled = trustEnabled;
  const res = await fn(payload);
  return res.data;
}

// ---------- Availability marks ----------

export const availabilityMarksRef = (uid) =>
  collection(db, "usersAvailability", uid, "availabilityMarks");

export const availabilityMarkRef = (uid, markId) =>
  doc(db, "usersAvailability", uid, "availabilityMarks", markId);

/**
 * Creates a new availability mark. Generates a markId via
 * `crypto.randomUUID()` so the caller doesn't need to.
 * Returns the generated id, or null on missing uid.
 */
export async function createAvailabilityMark(uid, payload) {
  if (!uid) return null;
  const markId = crypto.randomUUID();
  await setDoc(availabilityMarkRef(uid, markId), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return markId;
}

export async function updateAvailabilityMark(uid, markId, patch) {
  if (!uid || !markId) return;
  await setDoc(
    availabilityMarkRef(uid, markId),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function deleteAvailabilityMark(uid, markId) {
  if (!uid || !markId) return;
  await deleteDoc(availabilityMarkRef(uid, markId));
}

// ---------- Auto-vote settings ----------

export function autoVoteSettingsRef(uid) {
  if (!uid) return null;
  return doc(db, "usersAvailability", uid, "autoVoteSettings", "settings");
}

export async function setAutoVoteEnabled(uid, value) {
  if (!uid) return;
  const ref = autoVoteSettingsRef(uid);
  await setDoc(
    ref,
    { autoVoteEnabled: value === true, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// Phase 3: tier-based auto-vote vocabulary. Order matches the priority ladder
// described in docs/my-availability-phase-3-auto-vote-tiers-plan.md.
export const AUTO_VOTE_MODES = Object.freeze([
  "off",
  "cast",
  "submit",
  "submit-override",
]);

export function isValidAutoVoteMode(mode) {
  return typeof mode === "string" && AUTO_VOTE_MODES.includes(mode);
}

export async function setAutoVoteMode(uid, mode) {
  if (!isValidAutoVoteMode(mode)) {
    throw new Error(`Invalid autoVoteMode: ${mode}`);
  }
  if (!uid) return;
  const ref = autoVoteSettingsRef(uid);
  await setDoc(
    ref,
    { autoVoteMode: mode, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ---------- Auto-RSVP settings (mode + global Maybe/No target) ----------
// Stored alongside auto-vote settings on autoVoteSettings/settings.
export const AUTO_RSVP_MODES = Object.freeze(["off", "notify", "auto", "auto-override"]);
export const AUTO_RSVP_TARGETS = Object.freeze(["maybe", "no"]);

export function isValidAutoRsvpMode(mode) {
  return typeof mode === "string" && AUTO_RSVP_MODES.includes(mode);
}
export function isValidAutoRsvpTarget(target) {
  return typeof target === "string" && AUTO_RSVP_TARGETS.includes(target);
}

export async function setAutoRsvpMode(uid, mode) {
  if (!isValidAutoRsvpMode(mode)) {
    throw new Error(`Invalid autoRsvpMode: ${mode}`);
  }
  if (!uid) return;
  await setDoc(
    autoVoteSettingsRef(uid),
    { autoRsvpMode: mode, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function setAutoRsvpConflictTarget(uid, target) {
  if (!isValidAutoRsvpTarget(target)) {
    throw new Error(`Invalid autoRsvpConflictTarget: ${target}`);
  }
  if (!uid) return;
  await setDoc(
    autoVoteSettingsRef(uid),
    { autoRsvpConflictTarget: target, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function migrateLegacyAutoVote() {
  const fn = httpsCallable(functions, "migrateLegacyAutoVote");
  const res = await fn();
  return res.data;
}

export async function migrateAutoVoteMode() {
  const fn = httpsCallable(functions, "migrateAutoVoteMode");
  const res = await fn();
  return res.data;
}

// ---------- Backfill (auto-vote first-enable) ----------

export async function evaluateBackfillForUser() {
  const fn = httpsCallable(functions, "evaluateBackfillForUser");
  return fn();
}

export async function applyBackfillForUser(payload) {
  const fn = httpsCallable(functions, "applyBackfillForUser");
  return fn(payload);
}
